import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  emotionPessimistic,
  emotionBored,
  emotionAnxious,
  emotionSad,
  emotionDepressed,
  emotionStressed,
  emotionInadequate,
  emotionHopeful,
  emotionIndecisive,
  emotionHappy,
  emotionAngry,
} from "../../../assets";

const fetchInfoBoxes = async (page: number, limit = 11, t: (key: string) => string) => {
  await new Promise((resolve) => setTimeout(resolve, 500));

  const baseBoxes = [
    { id: 1, image: emotionPessimistic, emotionKey: "karamsar" },
    { id: 2, image: emotionBored, emotionKey: "canisikkin" },
    { id: 3, image: emotionAnxious, emotionKey: "endiseli" },
    { id: 4, image: emotionSad, emotionKey: "uzgun" },
    { id: 5, image: emotionDepressed, emotionKey: "depresif" },
    { id: 6, image: emotionStressed, emotionKey: "stresli" },
    { id: 7, image: emotionInadequate, emotionKey: "yetersiz" },
    { id: 8, image: emotionHopeful, emotionKey: "umutlu" },
    { id: 9, image: emotionIndecisive, emotionKey: "kararsiz" },
    { id: 10, image: emotionHappy, emotionKey: "mutlu" },
    { id: 11, image: emotionAngry, emotionKey: "kizgin" },
  ];

  return baseBoxes.map((box, index) => ({
    ...box,
    id: `${page}-${index}-${Date.now()}`,
    title: t(`emotion.${box.emotionKey}`),
    description: t(`description.${box.emotionKey}`),
  }));
};

interface InfoBox {
  id: string;
  image: string;
  emotionKey: string;
  title: string;
  description: string;
}

const HeroInfo = () => {
  const { t, i18n } = useTranslation();
  const [boxes, setBoxes] = useState<InfoBox[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const loadMoreBoxes = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const newBoxes = await fetchInfoBoxes(page, 11, t);
      if (newBoxes.length > 0) {
        setBoxes((prevBoxes) => [...prevBoxes, ...newBoxes]);
        setPage((prevPage) => prevPage + 1);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading info boxes:", error);
    } finally {
      setLoading(false);
    }
  }, [page, loading, hasMore, t]);

  useEffect(() => {
    setBoxes([]);
    setPage(1);
    setHasMore(true);
  }, [i18n.language]);

  useEffect(() => {
    if (boxes.length === 0) {
      loadMoreBoxes();
    }
  }, [boxes.length, loadMoreBoxes]);

  useEffect(() => {
    if (!containerRef.current || boxes.length === 0) return;

    const options = {
      root: containerRef.current,
      rootMargin: "0px",
      threshold: 1.0,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !loading) {
        loadMoreBoxes();
      }
    }, options);

    const currentObserver = observerRef.current;
    const children = containerRef.current.children;
    if (children.length > 0) {
      currentObserver.observe(children[children.length - 1]);
    }

    return () => {
      if (currentObserver) currentObserver.disconnect();
    };
  }, [loadMoreBoxes, hasMore, loading, boxes]);

  const scrollLeft = () => {
    if (containerRef.current) {
      const box = containerRef.current.firstChild as HTMLElement | null;
      if (box) {
        const boxWidth =
          box.offsetWidth + parseInt(window.getComputedStyle(containerRef.current).gap || "0");
        const newScrollLeft = containerRef.current.scrollLeft - boxWidth;
        containerRef.current.scrollTo({
          left: newScrollLeft,
          behavior: "smooth",
        });
      }
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      const box = containerRef.current.firstChild as HTMLElement | null;
      if (box) {
        const boxWidth =
          box.offsetWidth + parseInt(window.getComputedStyle(containerRef.current).gap || "0");
        const newScrollLeft = containerRef.current.scrollLeft + boxWidth;
        containerRef.current.scrollTo({
          left: newScrollLeft,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <div className="bg-custom-radial">
      <div className="relative w-[80%] sm:w-[82%] mx-auto flex items-center ">
        <button onClick={scrollLeft} className="absolute left-2 z-10 bg-black/40 rounded-full p-4">
          <ChevronLeft className="text-white" size={24} />
        </button>

        <button onClick={scrollRight} className="absolute right-2 z-10 bg-black/40 rounded-full p-4">
          <ChevronRight className="text-white" size={24} />
        </button>

        <div
          ref={containerRef}
          className="overflow-x-scroll hide-scrollbar no-scrollbar "
          style={{
            display: "flex",
            paddingTop: "5rem",
            paddingBottom: "5rem",
            gap: "1.25rem",
          }}
        >
          <AnimatePresence>
            {boxes.map((box) => (
              <motion.div
                key={box.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex-shrink-0 w-[100%] sm:w-[23%] text-white text-center bg-white bg-opacity-5 backdrop-blur-sm p-4 rounded-xl"
              >
                <div className="info-box-head flex flex-col items-center">
                  <div className="info-box-img py-2">
                    <img
                      className="h-[6rem] w-[6rem] object-cover rounded-full"
                      loading="lazy"
                      src={box.image}
                      alt={box.title}
                    />
                  </div>
                  <div className="info-box-title text-2xl">{box.title}</div>
                </div>
                <div className="info-box-desc mt-4">{box.description}</div>
              </motion.div>
            ))}
          </AnimatePresence>

          {loading && (
            <div className="flex items-center justify-center w-full p-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-white"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroInfo;
